import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShieldWarning, Scales, BookOpen, Warning } from '@phosphor-icons/react';
import { LEGAL_DOCS, LEGAL_DOC_BY_SLUG, LEGAL_VERSION, LEGAL_LAST_REVIEWED } from '../legal/content';

const Legal = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const doc = slug ? LEGAL_DOC_BY_SLUG[slug] : null;

  // Index page (no slug)
  if (!slug) {
    return (
      <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }} data-testid="legal-index-page">
        <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5" data-testid="back-to-home">
              <ArrowLeft size={14} /> Back to Fanzingy
            </button>
            <div className="text-xs text-white/70 font-bold uppercase tracking-wider">Legal</div>
          </div>
        </header>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 mb-4">
            <Scales size={14} weight="fill" className="text-[#facc15]" />
            <span className="text-[11px] uppercase tracking-wider font-bold">Legal Centre</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-2" style={{ letterSpacing: '-0.03em' }} data-testid="legal-title">
            Policies, agreements{' '}
            <span style={{ background: 'linear-gradient(90deg,#f472b6,#c084fc,#22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>&amp; disclosures.</span>
          </h1>
          <p className="text-white/60 text-base mb-2 font-light">Every clause, written in plain English. India + US covered.</p>
          <p className="text-[11px] text-white/70 mb-10 font-light">
            Document version <span className="font-mono">{LEGAL_VERSION}</span> · last reviewed {LEGAL_LAST_REVIEWED}
          </p>

          <DisclaimerBanner />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8" data-testid="legal-grid">
            {LEGAL_DOCS.map((d) => (
              <Link
                key={d.slug}
                to={`/legal/${d.slug}`}
                data-testid={`legal-card-${d.slug}`}
                className="text-left bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#c084fc]/50 hover:bg-white/[0.08] transition-all"
              >
                <div className="flex items-start gap-3">
                  <BookOpen size={20} className="text-[#facc15] mt-0.5 flex-shrink-0" weight="bold" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-extrabold mb-1 truncate">{d.title}</div>
                    <div className="text-xs text-white/70 font-light leading-relaxed">{d.short}</div>
                    {d.required && (
                      <span className="inline-block mt-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 font-bold">
                        Required at signup
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-xs text-white/70 leading-relaxed font-light border-t border-white/10 pt-6">
            <p><strong className="text-white/70">Registered office:</strong> Fanzingy Technologies Private Limited, India.</p>
            <p><strong className="text-white/70">Grievance Officer / DPO:</strong> dpo@fanzingy.com.</p>
            <p><strong className="text-white/70">DMCA / IP take-down:</strong> copyright@fanzingy.com.</p>
            <p><strong className="text-white/70">Privacy / Data requests:</strong> privacy@fanzingy.com.</p>
            <p className="mt-3 italic">All product, brand, and partner names appearing on this site are property of their respective owners. Reading these documents is not a substitute for independent legal advice.</p>
          </div>
        </div>
      </div>
    );
  }

  // Doc-not-found
  if (!doc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-white" style={{ background: '#080414' }}>
        <p className="text-white/60 mb-3">No such document.</p>
        <Link to="/legal" className="bg-white text-black px-4 py-2 rounded-full font-bold text-sm">Back to Legal Centre</Link>
      </div>
    );
  }

  // Single-doc page
  return (
    <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }} data-testid={`legal-doc-${slug}`}>
      <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/legal')} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5" data-testid="back-to-legal">
            <ArrowLeft size={14} /> Legal Centre
          </button>
          <div className="text-xs text-white/70 font-mono">v{LEGAL_VERSION}</div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2" style={{ letterSpacing: '-0.03em' }} data-testid="doc-title">{doc.title}</h1>
        <p className="text-white/60 text-base font-light mb-2">{doc.short}</p>
        <p className="text-[11px] text-white/70 mb-6 font-light">Effective {LEGAL_LAST_REVIEWED} · Document version {LEGAL_VERSION}</p>

        <DisclaimerBanner />

        <div className="space-y-6 mt-8" data-testid="doc-sections">
          {doc.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-base sm:text-lg font-extrabold mb-2 text-white">{s.h}</h2>
              <p className="text-sm text-white/75 leading-relaxed font-light whitespace-pre-wrap">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 border-t border-white/10 pt-6 flex flex-wrap items-center gap-3 text-xs text-white/70">
          <span>Questions?</span>
          <a href="mailto:grievance@fanzingy.com" className="text-[#c084fc] underline underline-offset-2">grievance@fanzingy.com</a>
          <span>·</span>
          <Link to="/legal" className="text-[#c084fc] underline underline-offset-2">Browse all policies</Link>
        </div>
      </article>
    </div>
  );
};

const DisclaimerBanner = () => (
  <div className="rounded-lg border border-amber-500/30 bg-amber-500/8 p-4 flex items-start gap-3" data-testid="legal-disclaimer">
    <Warning size={18} weight="fill" className="text-amber-300 flex-shrink-0 mt-0.5" />
    <div className="text-xs text-amber-100/90 leading-relaxed font-light">
      <strong className="text-amber-200">Template documents.</strong> These policies reflect Fanzingy's current operating model and the legal regimes we target (India + United States), but they are working drafts. Before signing them up to a transaction or relying on them in a dispute, please consult qualified counsel in your jurisdiction. Statutory references (PMLA, DPDPA, IT Rules, FEMA, Companies Act, SEC Reg D / Reg CF, FinCEN) are provided for transparency; the actual filings and licences are held by Fanzingy and its Partner NBFC.
    </div>
  </div>
);

export default Legal;
